import {
  sceneForBoardTemplate,
  type BoardObjectV2,
  type BoardTemplateId,
} from "@bezpart-board/shared";
import { nextZIndex, compareZIndex } from "../../editor/zIndex";
import { withGroupIdMeta } from "../../editor/grouping";

type Bounds = { x: number; y: number; w: number; h: number };

function expandBounds(b: Bounds, x: number, y: number): Bounds {
  const minX = Math.min(b.x, x);
  const minY = Math.min(b.y, y);
  const maxX = Math.max(b.x + b.w, x);
  const maxY = Math.max(b.y + b.h, y);
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function boundsForTemplateObject(obj: BoardObjectV2): Bounds {
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  let b: Bounds = { x: obj.x, y: obj.y, w: Math.max(w, 1), h: Math.max(h, 1) };
  const pts = (obj as { points?: [number, number][] }).points;
  if (Array.isArray(pts)) {
    for (const [px, py] of pts) {
      b = expandBounds(b, px, py);
    }
  }
  return b;
}

function unionTemplateBounds(objects: BoardObjectV2[]): Bounds | null {
  if (!objects.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const obj of objects) {
    const b = boundsForTemplateObject(obj);
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function shiftTemplateObject(obj: BoardObjectV2, dx: number, dy: number): BoardObjectV2 {
  const copy = JSON.parse(JSON.stringify(obj)) as BoardObjectV2;
  copy.id = crypto.randomUUID();
  copy.x = Math.round((copy.x || 0) + dx);
  copy.y = Math.round((copy.y || 0) + dy);
  const pts = (copy as { points?: [number, number][] }).points;
  if (Array.isArray(pts)) {
    (copy as { points: [number, number][] }).points = pts.map(
      ([px, py]) => [Math.round(px + dx), Math.round(py + dy)] as [number, number]
    );
  }
  return copy;
}

function liftWidgetAboveScene(
  objects: BoardObjectV2[],
  existingObjects: BoardObjectV2[]
): BoardObjectV2[] {
  if (!objects.length) return objects;
  const ordered = objects
    .map((obj, idx) => ({ obj, idx }))
    .sort((a, b) => {
      const byZ = compareZIndex(a.obj.zIndex ?? "", b.obj.zIndex ?? "");
      if (byZ !== 0) return byZ;
      return a.idx - b.idx;
    });

  const all = existingObjects.slice();
  const byId = new Map<string, string>();
  for (const item of ordered) {
    const z = nextZIndex(all);
    byId.set(item.obj.id, z);
    all.push({ zIndex: z } as BoardObjectV2);
  }

  return objects.map((obj) => ({
    ...obj,
    zIndex: byId.get(obj.id) ?? obj.zIndex,
  }));
}

/** Объекты шаблона со свежими id, центр блока — в (centerX, centerY). */
export function buildWidgetObjectsAtCenter(
  templateId: BoardTemplateId,
  centerX: number,
  centerY: number,
  existingObjects: BoardObjectV2[] = []
): BoardObjectV2[] {
  if (templateId === "empty") return [];
  const scene = sceneForBoardTemplate(templateId);
  if (!scene.objects.length) return [];

  const bounds = unionTemplateBounds(scene.objects);
  if (!bounds) return [];

  const cx = bounds.x + bounds.w / 2;
  const cy = bounds.y + bounds.h / 2;
  const dx = Math.round(centerX - cx);
  const dy = Math.round(centerY - cy);

  const groupId = crypto.randomUUID();
  const shifted = scene.objects.map((obj) => {
    const copy = shiftTemplateObject(obj, dx, dy);
    copy.meta = withGroupIdMeta(copy, groupId);
    return copy;
  });
  return liftWidgetAboveScene(shifted, existingObjects);
}
